import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Input, MessageBar, MessageBarBody, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, tokens } from "@fluentui/react-components";
import { api, type Gym } from "../lib/api.ts";
import { OpsPanel } from "../components/OpsPanel.tsx";
import { ConfirmButton } from "../components/ConfirmButton.tsx";

interface Stats {
  trainers: number;
  clients: number;
  gyms: number;
  unverifiedGyms: number;
  checkInsThisWeek: number;
}
interface TrainerRow {
  id: string;
  createdAt: string;
  user: { email: string; name: string };
  subscription: { status: string } | null;
  _count: { clients: number };
}

export function AdminPage() {
  const qc = useQueryClient();
  const stats = useQuery({ queryKey: ["admin", "stats"], queryFn: () => api.get<Stats>("/api/admin/stats") });
  const trainers = useQuery({ queryKey: ["admin", "trainers"], queryFn: () => api.get<TrainerRow[]>("/api/admin/trainers") });
  const gyms = useQuery({ queryKey: ["admin", "gyms"], queryFn: () => api.get<Gym[]>("/api/admin/gyms/unverified") });
  const delTrainer = useMutation({ mutationFn: (id: string) => api.del(`/api/admin/trainers/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin"] }) });
  const verify = useMutation({ mutationFn: (id: string) => api.post(`/api/admin/gyms/${id}/verify`), onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin"] }) });

  const tile = (label: string, v: number | undefined) => (
    <Card style={{ padding: 16 }}>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{label}</Text>
      <Text size={800} weight="semibold">{v ?? "–"}</Text>
    </Card>
  );

  return (
    <div>
      <Text as="h1" size={800} weight="semibold" block style={{ marginBottom: 16 }}>
        Admin
      </Text>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        {tile("Trainers", stats.data?.trainers)}
        {tile("Active clients", stats.data?.clients)}
        {tile("Gyms", stats.data?.gyms)}
        {tile("Check-ins (7d)", stats.data?.checkInsThisWeek)}
      </div>

      <OpsPanel />

      <Text size={500} weight="semibold" block>Trainers</Text>
      <Table aria-label="Trainers" style={{ marginBottom: 24, tableLayout: "fixed" }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Clients</TableHeaderCell>
            <TableHeaderCell>Plan</TableHeaderCell>
            <TableHeaderCell>Joined</TableHeaderCell>
            <TableHeaderCell style={{ width: 90 }}></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(trainers.data ?? []).map((t) => (
            <TableRow key={t.id}>
              <TableCell>{t.user.name}</TableCell>
              <TableCell style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.user.email}</TableCell>
              <TableCell>{t._count.clients}</TableCell>
              <TableCell>{t.subscription?.status ?? "—"}</TableCell>
              <TableCell>{new Date(t.createdAt).toLocaleDateString("en-GB")}</TableCell>
              <TableCell>
                <ConfirmButton size="small" appearance="subtle" title={`Delete ${t.user.name}?`} message={`Erases this trainer, their ${t._count.clients} client(s) and all schedules. Cannot be undone.`} confirmLabel="Delete" onConfirm={() => delTrainer.mutate(t.id)}>
                  Delete
                </ConfirmButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Text size={500} weight="semibold" block>Gyms awaiting verification ({gyms.data?.length ?? 0})</Text>
      <Table aria-label="Unverified gyms">
        <TableBody>
          {(gyms.data ?? []).map((g) => (
            <TableRow key={g.id}>
              <TableCell>{g.name}</TableCell>
              <TableCell>{[g.city, g.postcode].filter(Boolean).join(", ")}</TableCell>
              <TableCell>
                <Button size="small" onClick={() => verify.mutate(g.id)}>Verify</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <PurgeAccounts />
    </div>
  );
}

/** Danger zone: delete every account except the admin's. Typed confirmation; shows the result. */
function PurgeAccounts() {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const others = useQuery({ queryKey: ["admin", "others"], queryFn: () => api.get<{ trainers: number; users: number }>("/api/admin/accounts/others") });
  const purge = useMutation({
    mutationFn: () => api.post<{ trainersDeleted: number; clientsErased: number; otherUsersDeleted: number }>("/api/admin/accounts/purge", { confirm: "DELETE" }),
    onSuccess: () => {
      setTyped("");
      void qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  return (
    <Card style={{ padding: 16, marginTop: 24, border: `1px solid ${tokens.colorPaletteRedBorder2}`, display: "flex", flexDirection: "column", gap: 10 }}>
      <Text size={500} weight="semibold">Danger zone</Text>
      <Text>
        Delete every account except yours — {others.data ? `${others.data.trainers} trainer account(s) and ${others.data.users} login(s) in total` : "…"}. Their clients, schedules,
        messages and usage data are erased the same way as when a trainer deletes their own account. Your own account, clients and setup are kept. This can't be undone.
      </Text>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Field label='Type DELETE to confirm' size="small">
          <Input size="small" value={typed} onChange={(_, d) => setTyped(d.value)} aria-label="Type DELETE to confirm" />
        </Field>
        <Button appearance="primary" style={{ backgroundColor: typed === "DELETE" ? tokens.colorPaletteRedBackground3 : undefined }} disabled={typed !== "DELETE" || purge.isPending || !others.data?.users} onClick={() => purge.mutate()}>
          {purge.isPending ? "Deleting…" : "Delete all other accounts"}
        </Button>
      </div>
      {purge.isSuccess && (
        <MessageBar intent="success">
          <MessageBarBody>Done — {purge.data.trainersDeleted} trainer account(s) and {purge.data.clientsErased} client(s) erased, {purge.data.otherUsersDeleted} other login(s) removed.</MessageBarBody>
        </MessageBar>
      )}
      {purge.isError && <MessageBar intent="error"><MessageBarBody>{(purge.error as Error).message}</MessageBarBody></MessageBar>}
    </Card>
  );
}
